/** Platform admin roles (Firebase custom claim `rentalyzerRole`). */
export const ADMIN_ROLES = ['super_admin', 'admin', 'support'];

export const ADMIN_SHELL_ROLES = ['super_admin', 'admin', 'support'];

export const IMPERSONATION_ROLES = ['super_admin', 'admin'];

export const ROLE_MANAGER_ROLES = ['super_admin'];

export const AUDIT_READ_ROLES = ['super_admin', 'admin'];

export const ACTIVITY_READ_ROLES = ['super_admin', 'admin'];

/** Admin Prospection (adminProspects). support is excluded. Keep in sync with functions/admin/roles.js */
export const PROSPECTION_READ_ROLES = ['super_admin', 'admin'];
export const PROSPECTION_WRITE_ROLES = ['super_admin', 'admin'];

/**
 * @param {unknown} value
 * @returns {'super_admin' | 'admin' | 'support' | null}
 */
export function normalizeAdminRole(value) {
  if (value === 'super_admin' || value === 'admin' || value === 'support') return value;
  return null;
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canAccessAdminShell(role) {
  return ADMIN_SHELL_ROLES.includes(role);
}

/**
 * Impersonation is a full target session. support is never allowed.
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canImpersonate(role) {
  return IMPERSONATION_ROLES.includes(role);
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canManageRoles(role) {
  return ROLE_MANAGER_ROLES.includes(role);
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canReadAudit(role) {
  return AUDIT_READ_ROLES.includes(role);
}

/**
 * Product activity analytics (getAdminActivitySummary). support is excluded.
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canReadActivity(role) {
  return ACTIVITY_READ_ROLES.includes(role);
}

/**
 * Prospection CRM (list/get). support is excluded — third-party owner PII.
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canReadProspection(role) {
  return PROSPECTION_READ_ROLES.includes(role);
}

/**
 * Prospection CRM (create/update/delete). v1 same as read.
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function canWriteProspection(role) {
  return PROSPECTION_WRITE_ROLES.includes(role);
}

/**
 * @param {string | null | undefined} raw
 * @returns {Set<string>}
 */
export function parseAdminUidList(raw) {
  if (!raw || typeof raw !== 'string') return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

/**
 * Shell access: custom claim first, then legacy UID allowlist.
 * Impersonation sessions never get the admin shell.
 *
 * @param {{
 *   adminRole?: string | null,
 *   impersonationActive?: boolean,
 *   uid?: string | null,
 *   legacyUidSet?: Set<string> | null,
 * }} params
 * @returns {boolean}
 */
export function resolveAdminShellAccess({
  adminRole = null,
  impersonationActive = false,
  uid = null,
  legacyUidSet = null,
} = {}) {
  if (impersonationActive) return false;
  if (canAccessAdminShell(adminRole)) return true;
  if (uid && legacyUidSet && legacyUidSet.has(uid)) return true;
  return false;
}
